/**
 * Session storage utilities for persisting context across the auth flow.
 * Stores EntryPoint and IdentificationMethod so they can be included in all events.
 */

import { getSessionStorageItemSafely, removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../utils/storage';
import type { EntryPoint, IdentificationMethod } from './types';

const STORAGE_KEY = 'auth_analytics_context';

interface SessionContext {
  entryPoint?: EntryPoint;
  identificationMethod?: IdentificationMethod;
  callingFlow?: EntryPoint;
}

/**
 * Get the current session context from session storage.
 */
export const getSessionContext = (): SessionContext => {
  try {
    const stored = getSessionStorageItemSafely(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Set the entry point for the current auth-app visit - the first page the user actually
 * landed on (sign-in vs. sign-up), kept for the whole visit even if they switch pages
 * before completing it. Only set if not already set, so a later page in the same visit
 * can't overwrite the true first touch. Relies on clearSessionContext() being called when
 * the visit ends (completeAuthRedirect, logout) so a later, unrelated visit in the same
 * tab (e.g. redirected back after a platform-app session timeout) starts fresh instead of
 * inheriting this one's value.
 *
 * Not a substitute for knowing which flow a specific action belongs to - callers that need
 * that (e.g. whether a specific MFA token came from a login or a registration) should use
 * setCallingFlow/getCallingFlow below instead, since it can genuinely differ from the visit's
 * first touch (e.g. a login reached via the sign-up page's userAlreadyValidated branch).
 */
export const setEntryPoint = (entryPoint: EntryPoint): void => {
  const context = getSessionContext();
  if (!context.entryPoint) {
    context.entryPoint = entryPoint;
    setSessionStorageItemSafely(STORAGE_KEY, JSON.stringify(context));
  }
};

/**
 * Get the entry point from session storage.
 * Defaults to 'sign-in' if not set.
 */
export const getEntryPoint = (): EntryPoint => {
  return getSessionContext().entryPoint ?? 'sign-in';
};

/**
 * Set which flow a specific in-progress action belongs to (sign-in vs. sign-up) - unlike
 * setEntryPoint, always overwrites, since a new action can genuinely belong to a different
 * flow than the one before it in the same tab (e.g. an abandoned sign-in followed by a
 * sign-up). Callers (LoginPage, SignUpPage) set this once per action; downstream pages
 * (MFAChallengePage, MfaEnrollOfferPage, MfaEnrollConfirmPage) read it for the rest of that
 * action's lifetime, including across page reloads, since sessionStorage survives those.
 * Cleared along with the rest of the context by clearSessionContext() so it can't leak into
 * a later, unrelated action in the same tab.
 */
export const setCallingFlow = (callingFlow: EntryPoint): void => {
  const context = getSessionContext();
  context.callingFlow = callingFlow;
  setSessionStorageItemSafely(STORAGE_KEY, JSON.stringify(context));
};

/**
 * Get the calling flow from session storage, or undefined if no action has set one yet
 * (e.g. a direct/bookmarked load of an MFA page with no preceding Login/SignUp action).
 */
export const getCallingFlow = (): EntryPoint | undefined => {
  return getSessionContext().callingFlow;
};

/**
 * Set the identification method for the current session.
 * Should be called when user chooses how to authenticate (manually, SSO, etc.)
 */
export const setIdentificationMethod = (method: IdentificationMethod): void => {
  const context = getSessionContext();
  context.identificationMethod = method;
  setSessionStorageItemSafely(STORAGE_KEY, JSON.stringify(context));
};

/**
 * Get the identification method from session storage.
 */
export const getIdentificationMethod = (): IdentificationMethod | undefined => {
  return getSessionContext().identificationMethod;
};

/**
 * Clear the session context (e.g., after successful login or on logout).
 */
export const clearSessionContext = (): void => {
  removeSessionStorageItemSafely(STORAGE_KEY);
};
